import React, { useState } from "react";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import PageHeader from "../common/PageHeader";

// MUI Imports
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import BarChartIcon from "@mui/icons-material/BarChart";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FilterListIcon from "@mui/icons-material/FilterList";

// Custom hooks and utilities
import { useOrdersHistory } from "./hooks/useOrdersHistory";
import { downloadOrdersPDF } from "./utils/pdfUtils";

// Components
import OrderFilters from "./components/OrderFilters";
import OrderActions from "./components/OrderActions";
import OrdersTable from "./components/OrdersTable";
import AggregatedTable from "./components/AggregatedTable";
import OrderSummaryModal from "./components/OrderSummaryModal";
import OrderViewModal from "./OrderViewModal";

const OrdersHistory = () => {
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  // Custom hook for data management
  const {
    filtered,
    search,
    setSearch,
    status,
    setStatus,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    showAllDates,
    setShowAllDates,
    orderType,
    setOrderType,
    orderTypeOptions,
    aggregatedData,
  } = useOrdersHistory();

  // Local state
  const [showSummary, setShowSummary] = useState(false);
  const [showAggregatedView, setShowAggregatedView] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // New state for filter visibility
  const [viewOrder, setViewOrder] = useState(null);

  const handleDownloadPDF = () => {
    downloadOrdersPDF(filtered, currencySymbol);
  };

  // Calculate totals for header
  const totalSales = filtered.reduce(
    (sum, order) => sum + (Number(order.amount) || 0),
    0,
  );
  const totalOrders = filtered.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3">
      {/* Page Header */}
      <PageHeader
        title="Order History"
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
            View and manage all your order records
          </span>
        }
      />

      {/* Sales Overview Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        {/* Total Sales Card */}
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">Total Sales</div>
          </div>
          <div className="text-white text-lg lg:text-xl font-bold tracking-tight">
            {currencySymbol}
            {totalSales.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>

        {/* Total Orders Card */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">
              Total Orders
            </div>
          </div>
          <div className="text-white text-lg lg:text-xl font-bold tracking-tight">
            {totalOrders}
          </div>
        </div>

        {/* Date Range Info Card */}
        <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">Date Range</div>
          </div>
          <div className="text-white text-sm lg:text-base font-semibold tracking-tight">
            {showAllDates
              ? "All Dates"
              : `${fromDate?.toLocaleDateString("en-GB") || "N/A"} - ${toDate?.toLocaleDateString("en-GB") || "N/A"}`}
          </div>
        </div>
      </div>

      {/* Collapsible Filters & Actions Bar - Apple inspired */}
      <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-sm border border-gray-200/60 overflow-hidden mb-4 hover:shadow-lg transition-all duration-300">
        {/* Filter Header - Apple style */}
        <div
          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-all duration-300 group bg-white"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200">
              <FilterListIcon className="text-white w-4 h-4" />
            </div>
            <div className="flex flex-col">
              <span className="font-display font-semibold text-sm text-gray-900 tracking-tight">
                Filters & Actions
              </span>
              <span className="font-text text-xs text-gray-500">
                {showFilters
                  ? "Refine your search criteria"
                  : "Click to customize your view"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs font-text text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {filtered.length} results
            </div>
            <IconButton
              size="small"
              className="text-gray-500 hover:text-blue-500 transition-all duration-200 hover:bg-blue-50 rounded-lg p-1"
            >
              {showFilters ? (
                <KeyboardArrowUpIcon className="w-4 h-4" />
              ) : (
                <KeyboardArrowDownIcon className="w-4 h-4" />
              )}
            </IconButton>
          </div>
        </div>

        {/* Collapsible Content - Scrollable on Mobile */}
        <div
          className={`transition-all duration-500 ease-out overflow-hidden ${showFilters ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
            }`}
        >
          <div className="border-t border-gray-100/60">
            {/* Scrollable container for mobile */}
            <div className="px-3 pb-3 pt-2 max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <div className="flex flex-col gap-2">
                {/* Filters Section */}
                <OrderFilters
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  showAllDates={showAllDates}
                  setShowAllDates={setShowAllDates}
                  status={status}
                  setStatus={setStatus}
                  orderType={orderType}
                  setOrderType={setOrderType}
                  orderTypeOptions={orderTypeOptions}
                  search={search}
                  setSearch={setSearch}
                />

                {/* Actions Section */}
                <OrderActions
                  onDownloadPDF={handleDownloadPDF}
                  onShowSummary={() => setShowSummary(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONDITIONAL RENDERING: Show Aggregated View OR Detailed View */}
      {showAggregatedView ? (
        <AggregatedTable
          aggregatedData={aggregatedData}
          currencySymbol={currencySymbol}
          showAggregatedView={showAggregatedView}
          onToggleView={() => setShowAggregatedView(!showAggregatedView)}
        />
      ) : (
        <OrdersTable
          orders={filtered}
          currencySymbol={currencySymbol}
          showAggregatedView={showAggregatedView}
          onToggleView={() => setShowAggregatedView(!showAggregatedView)}
          onViewOrder={(order) => setViewOrder(order)}
        />
      )}

      {/* Summary Modal */}
      <OrderSummaryModal
        isOpen={showSummary}
        onClose={() => setShowSummary(false)}
        filtered={filtered}
      />
      {viewOrder && (
        <OrderViewModal
          order={viewOrder}
          onClose={() => setViewOrder(null)}
        />
      )}

    </div>
  );
};

export default OrdersHistory;
